/**
 * Checkout form contract: zod schema factories, inferred value types, and empty defaults.
 * Owns the shape of the enrollee + dependents data so {@link enrollModel} and the form stay in
 * sync from one source of truth. Date of hire and SMS consent are shown per group (via checkout
 * config) but are always optional, so validation is the same for every group.
 */
import {differenceInYears, format, isValid, parseISO} from 'date-fns';
import {z} from 'zod';
import {parseUsPhone} from '@common/lib/utils';

export const RELATIONSHIP_OPTIONS = [
  {value: 'spouse', label: 'Spouse'},
  {value: 'child', label: 'Child'},
  {value: 'domestic-partner', label: 'Domestic partner'},
  {value: 'other', label: 'Other'},
] as const;

export const BENEFICIARY_RELATIONSHIP_OPTIONS = [
  {value: 'spouse', label: 'Spouse'},
  {value: 'child', label: 'Child'},
  {value: 'parent', label: 'Parent'},
  {value: 'sibling', label: 'Sibling'},
  {value: 'domestic-partner', label: 'Domestic partner'},
  {value: 'other', label: 'Other'},
] as const;

export const GENDER_OPTIONS = [
  {value: 'M', label: 'Male'},
  {value: 'F', label: 'Female'},
] as const;

/** The employee must be at least this old to enroll. */
const MIN_PRIMARY_AGE = 16;
/** Upper sanity bound to catch typo'd birth years (e.g. 1900), not a real eligibility limit. */
const MAX_AGE_YEARS = 120;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Parse a `yyyy-MM-dd` string to a Date, rejecting non-real dates (e.g. Feb 30 rolls over). */
function parseBirthDate(value: string): Date | null {
  if (!ISO_DATE.test(value)) return null;
  const date = parseISO(value);
  if (!isValid(date) || format(date, 'yyyy-MM-dd') !== value) return null;
  return date;
}

const ssn = z
  .string()
  .trim()
  .refine((v) => /^\d{3}-?\d{2}-?\d{4}$/.test(v), 'Enter a valid SSN');

const gender = z.string().refine((v) => GENDER_OPTIONS.some((option) => option.value === v), 'Required');

/**
 * A birth date field: required, a real non-future calendar date within a sane range. Pass `minAge`
 * to also require a minimum age as of today (used for the primary applicant). `optional` allows it
 * blank but still checks it when filled in.
 */
function birthDate(minAge?: number, {optional = false}: {optional?: boolean} = {}) {
  return (optional ? z.string() : z.string().min(1, 'Required'))
    .superRefine((value, ctx) => {
      if (!value) return; // empty is already reported by min(1)
      const date = parseBirthDate(value);
      if (!date) {
        ctx.addIssue({code: z.ZodIssueCode.custom, message: 'Enter a valid date'});
        return;
      }
      const now = new Date();
      if (date > now) {
        ctx.addIssue({code: z.ZodIssueCode.custom, message: "Date of birth can't be in the future"});
        return;
      }
      const age = differenceInYears(now, date);
      if (age > MAX_AGE_YEARS) {
        ctx.addIssue({code: z.ZodIssueCode.custom, message: 'Enter a valid date of birth'});
        return;
      }
      if (minAge != null && age < minAge) {
        ctx.addIssue({code: z.ZodIssueCode.custom, message: `Must be at least ${minAge} years old`});
      }
    });
}

export const dependentSchema = z.object({
  firstName: z.string().trim().min(1, 'Required'),
  lastName: z.string().trim().min(1, 'Required'),
  relationship: z.string().min(1, 'Required'),
  dob: birthDate(),
  gender,
  ssn,
});

export function buildPersonalInfoSchema() {
  return z.object({
    firstName: z.string().trim().min(1, 'Required'),
    lastName: z.string().trim().min(1, 'Required'),
    dob: birthDate(MIN_PRIMARY_AGE),
    gender,
    dateOfHire: z.string(),
    ssn: z
      .string()
      .trim()
      .refine((v) => /^\d{3}-?\d{2}-?\d{4}$/.test(v), 'Enter a valid Social Security number'),
    email: z.string().trim().email('Valid email required'),
    phone: z
      .string()
      .trim()
      .refine((v) => parseUsPhone(v) !== null, 'Valid phone required'),
    address1: z.string().trim().min(1, 'Required'),
    address2: z.string().trim(),
    city: z.string().trim().min(1, 'Required'),
    state: z.string().min(2, 'Enter a state code'),
    zip: z.string().regex(/^\d{5}$/, '5-digit ZIP'),
  });
}

/** The fields e123's member BENEFICIARY object takes. */
export const beneficiarySchema = z.object({
  firstName: z.string().trim().min(1, 'Required'),
  lastName: z.string().trim().min(1, 'Required'),
  relationship: z.string().min(1, 'Required'),
  // The rest are optional to e123; checked only when filled in.
  dob: birthDate(undefined, {optional: true}),
  phone: z
    .string()
    .trim()
    .refine((v) => !v || parseUsPhone(v) !== null, 'Valid phone required'),
  // e123 takes a single address line for a beneficiary.
  address: z.string().trim(),
  city: z.string().trim(),
  state: z.string(),
  zip: z.string().regex(/^(\d{5})?$/, '5-digit ZIP'),
});

/** `beneficiary` is validated only when an elected product collects one. */
export function buildCheckoutSchema({beneficiary = false}: {beneficiary?: boolean} = {}) {
  return buildPersonalInfoSchema().extend({
    dependents: z.array(dependentSchema),
    beneficiary: beneficiary ? beneficiarySchema : z.custom<Beneficiary>(),
    // SMS consent is optional opt-in — users can submit whether or not it's checked.
    smsConsent: z.boolean(),
  });
}

export type Dependent = z.infer<typeof dependentSchema>;
export type Beneficiary = z.infer<typeof beneficiarySchema>;
export type PersonalInfo = z.infer<ReturnType<typeof buildPersonalInfoSchema>>;
export type CheckoutValues = PersonalInfo & {
  dependents: Dependent[];
  beneficiary: Beneficiary;
  smsConsent: boolean;
};

export const EMPTY_PERSONAL_INFO: PersonalInfo = {
  firstName: '',
  lastName: '',
  dob: '',
  gender: '',
  dateOfHire: '',
  ssn: '',
  email: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
};

export const EMPTY_DEPENDENT: Dependent = {
  firstName: '',
  lastName: '',
  relationship: '',
  dob: '',
  gender: '',
  ssn: '',
};

export const EMPTY_BENEFICIARY: Beneficiary = {
  firstName: '',
  lastName: '',
  relationship: '',
  dob: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
};
